import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Calendar, User, ChevronRight, AlertTriangle, ChevronLeft } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PendingCheckout } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface PendingCheckoutsWidgetProps {
  checkouts: PendingCheckout[];
  loading?: boolean;
}

const PendingCheckoutsWidget: React.FC<PendingCheckoutsWidgetProps> = ({
  checkouts,
  loading = false
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Calculate pagination
  const totalPages = Math.ceil(checkouts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCheckouts = checkouts.slice(startIndex, endIndex);
  
  // Reset to first page when checkouts change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [checkouts.length]);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm h-full">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            Check-outs Pendentes
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-4">
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          Check-outs Pendentes
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/classes')}
          className="h-7 px-2 text-sm font-extrabold text-orange-600 hover:text-orange-700 hover:bg-orange-50"
        >
          Ver todas
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-4 h-[calc(100%-55px)] flex flex-col">
        {checkouts.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhum check-out pendente</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 flex-1 overflow-y-auto">
              {currentCheckouts.map((checkout) => (
                <div
                  key={checkout.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors min-h-[80px]"
                  onClick={() => navigate(`/classes/${checkout.class.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-extrabold truncate">
                        {checkout.class.course.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 mb-1 text-xs text-muted-foreground">
                      {checkout.org_name && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {checkout.org_name}
                        </div>
                      )}
                      {checkout.instructor && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {checkout.instructor.first_name} {checkout.instructor.last_name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(parseISO(checkout.start_date), "dd/MM 'às' HH:mm", { locale: ptBR })}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {checkout.duration_minutes}min
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 pt-3 border-t">
                <Pagination>
                  <PaginationContent className="justify-center">
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingCheckoutsWidget;
